<template>
  <v-timeline-item
    :class="['mb-2', computedTimelineItemClasses]"
    :id="isSelected ? 'scrollable-general' : ''"
    :dot-color="variables.newSelected"
    fill-dot
    size="9px"
  >
    <div
      class="flux-element__header cursor-pointer"
      @click="() => (isSelected ? selectOp() : selectOp(op))"
    >
      <span :class="lightOpacity" class="fbody-1">
        <DevHelper> ({{ op.op_id }}) </DevHelper>

        <div class="fd-flex-center">
          <div
            class="fbody-1 semi-bold fd-flex-center gap-4 text-neutral-secondary position-relative"
          >
            <div>
              {{ computedOpName }}
            </div>

            <a v-if="op.link_url" :href="op.link_url" target="_blank">
              <vue-feather tag="div" type="external-link" size="12" />
            </a>

            <EntityDetailsTooltip
              v-if="clientParameters.display_operation_details"
              :entity="fullOP"
              :displayed-keys="[
                'op_id',
                'op_sequence',
                ...(clientParameters.has_conwip ? ['operator', 'machine'] : []),
                ...Object.keys(op.extra_infos || {}),
              ]"
            />

            <OperationEventsModal v-if="isOplitAdmin" :operation="op" />
          </div>
        </div>

        <div class="fcaption text-neutral-secondary">
          <v-tooltip
            location="bottom"
            content-class="px-2 py-1"
            :z-index="zIndex"
          >
            <template v-slot:activator="{props}">
              <span v-bind="props">
                <span v-if="opDoneDate">
                  {{ $t("operation.done_at") }}
                  {{ prettyOpDoneDate }}
                </span>
                <span
                  v-else
                  :data-end-date="computedFluxDate"
                  :data-erp-date="computedErpDate"
                >
                  {{ $t("scheduling.end_estimated_on") }}
                  {{ dateFormat(computedFluxDate, "DD/MM/YYYY") }}
                </span>
              </span>
            </template>
            <div>
              <div v-if="computedErpDate">
                {{ $t("operation.status.erp-date") }}
                <strong>
                  {{ dateFormat(computedErpDate, "DD/MM/YYYY") }}</strong
                >
              </div>
              <div v-if="computedDate">
                {{ $t("operation.planned_at") }}
                <strong>
                  {{ dateFormat(computedDate, "DD/MM/YYYY") }}
                </strong>
              </div>
              <div>
                {{
                  opDoneDate
                    ? $t("operation.done_at")
                    : $t("scheduling.end_estimated_on")
                }}
                <strong>
                  {{ dateFormat(computedFluxDate, "DD/MM/YYYY") }}
                </strong>
              </div>
              <div v-if="computedOperationRealDelta">
                {{ $t("operation.status.erp_date_diff") }} :
                <strong>
                  {{ computedOperationRealDelta }}{{ $t("global.day_letter") }}
                </strong>
              </div>
              <div v-if="shouldDisplayStatusAvailableAtInTooltip">
                {{ $t("operation.available_on") }}
                <strong>
                  {{ dateFormat(op.status_available_at, "DD/MM/YYYY") }}
                </strong>
              </div>
            </div>
          </v-tooltip>
          <span
            v-if="computedOperationDelay"
            :class="[
              'ml-1',
              computedOperationIsLate
                ? 'text-newPinkRegular'
                : 'text-newGreenRegular',
            ]"
          >
            {{ computedOperationDelay }}
          </span>
        </div>
      </span>

      <div :class="['fd-flex-center justify-end op-chip ml-8', lightOpacity]">
        <div v-if="!isSelected">
          <v-tooltip
            v-if="op.new_fault || op.fault"
            location="top"
            :z-index="zIndex"
          >
            <template v-slot:activator="{props}">
              <v-img
                v-bind="props"
                width="13"
                :src="getAssetURL('incident-signe.svg')"
                cover
              />
            </template>
            <div>{{ $t("operation.fault") }}</div>
          </v-tooltip>
        </div>

        <div class="flux-element__quantity-section mx-2">
          <div
            v-for="(quantityInfo, index) in displayedQuantities"
            v-tooltip="quantityInfo.tooltip"
            :key="index"
            :class="[
              'flux-element__quantity-block',
              'text-14',
              ...quantityInfo.classes,
            ]"
          >
            <OpalIcon :name="quantityInfo.icon" size="16" />

            <span class="ml-1">
              {{ quantityInfo.value }}
            </span>
          </div>
        </div>

        <OperationStatus
          :operation="op"
          :disabled="!canUpdateStatus"
          @change-status="({new_status}) => handleStatusChange(new_status)"
        />

        <vue-feather
          class="mx-2"
          :type="isSelected ? 'chevron-up' : 'chevron-down'"
          size="18px"
        />
      </div>
    </div>

    <FluxDataBody
      v-if="isSelected"
      class="mt-2"
      :op="fullOP"
      :next-op="nextOp"
      :propagate-ops-changes="propagateOpsChanges"
      :z-index="zIndex"
      :can-make-change="!readOnly"
      :force-loading="isLoading"
      @start-operation="() => handleStatusChange(OF_STATUS.ON_GOING)"
    />
  </v-timeline-item>
</template>
<script lang="ts">
import {PropType, defineComponent, toRefs, computed, ref} from "vue";
import {storeToRefs} from "pinia";
import {useI18n} from "vue-i18n";
import moment from "moment";
import _ from "lodash";
import {
  getReadableImportParsingRuleValue,
  pgOpsMapFn,
  getEstimatedEndDate,
  getOperationStatus,
} from "@oplit/shared-module";
import FluxDataBody from "@/domains/scheduling/domains/of-sidebar/components/FluxDataBody.vue";
import OperationStatus from "@/domains/scheduling/components/operations/OperationStatus.vue";
import EntityDetailsTooltip from "@/components/Commons/EntityDetailsTooltip.vue";
import OperationEventsModal from "@/domains/scheduling/domains/of-sidebar/components/OperationEventsModal.vue";
import {
  TrackOptionsCtaOrigin,
  useSchedulingOperationStatusChange,
} from "@/composables/scheduling/useSchedulingOperationStatusChange";
import {useOperationStatus} from "@/composables/scheduling/useOperationStatus";
import {useUserStore} from "@/lib/stores/userStore";
import {useMainStore} from "@/stores/mainStore";
import {useSchedulingStore} from "@/stores/schedulingStore";
import {usePermissionsStore} from "@/lib/stores/permissionsStore";
import {CSS_FLUX_SELECTED_OP_CLASS, OF_STATUS} from "@/config/constants";
import {dateFormat} from "@/tscript/utils/dateUtils";
import {getAssetURL} from "@/tscript/utils/generalHelpers";
import type {SchedulingOperation} from "@/interfaces";

export default defineComponent({
  name: "flux-element",
  components: {
    EntityDetailsTooltip,
    FluxDataBody,
    OperationStatus,
    OperationEventsModal,
  },
  props: {
    isSelected: {type: Boolean, default: false},
    op: {
      type: Object as PropType<SchedulingOperation>,
      default: () => ({} as SchedulingOperation),
    },
    nextOp: {
      type: Object as PropType<SchedulingOperation>,
      default: undefined,
    },
    initialOp: {type: Object, default: () => ({})},
    selectedOpId: {type: String},
    selectOp: {type: Function, default: () => void 0},
    propagateOpsChanges: {type: Function, default: () => void 0},
    zIndex: {type: Number, default: 2000},
    readOnly: {type: Boolean, default: false},
    index: {type: Number, default: -1},
  },
  setup(props) {
    const {t} = useI18n();

    const {clientParameters, variables} = storeToRefs(useMainStore());
    const schedulingStore = useSchedulingStore();
    const {sendSchedulingLoadEventToBackend} = schedulingStore;
    const {currentPermissions} = storeToRefs(usePermissionsStore());

    const {handleOperationsStatusChangeConfirmation} =
      useSchedulingOperationStatusChange({
        trackOptions: {cta_origin: TrackOptionsCtaOrigin.Sidebar},
      });

    const {op} = toRefs(props);
    const {displayedOperationStatus} = useOperationStatus(op);
    const {isOplitAdmin} = storeToRefs(useUserStore());

    const isLoading = ref(false);

    const opDoneDate = computed(() => {
      const {
        op: {status_done_at, op_status, new_status, day_date, new_date},
      } = props;
      if (getOperationStatus({op_status, new_status}) !== OF_STATUS.DONE)
        return;
      return status_done_at || new_date || day_date;
    });
    const shouldDisplayStatusAvailableAtInTooltip = computed(() => {
      if (props.index !== 0) return false;
      if (!props.op?.status_available_at) return false;
      return true;
    });

    const fullOP = computed<SchedulingOperation>(() => {
      return {
        ...(props.op.extra_infos || {}),
        ...(props.op.internal_extra_infos || {}),
        ...(props.op || {}),
      };
    });
    const disabledStatusPicker = computed(() => {
      return props.readOnly || fullOP.value.erp_status === OF_STATUS.DONE;
    });
    const canUpdateStatus = computed(() => {
      return (
        !disabledStatusPicker.value ||
        currentPermissions.value.scheduling.update_of_status
      );
    });

    const displayedQuantities = computed(() => {
      const {enable_partial_op_validation} = clientParameters.value;
      const {quantite_of_disponible, quantity_produced, quantite_op_rebut} =
        fullOP.value;

      let quantities = [];
      if (
        enable_partial_op_validation &&
        quantite_of_disponible &&
        ([OF_STATUS.AVAILABLE, OF_STATUS.ON_GOING] as string[]).includes(
          fullOP.value.op_status,
        )
      ) {
        quantities.push({
          icon: "box-play",
          value: +quantite_of_disponible,
          tooltip: t("scheduling.sidebar__header.on_going_quantity"),
          classes: [
            "flux-element__quantity-block--neutral",
            "bg-neutral-tertiary",
            "text-neutral-secondary",
          ],
        });
      }
      if (quantity_produced) {
        quantities.push({
          icon: "box-checked",
          value: +quantity_produced,
          tooltip: t("scheduling.sidebar__header.done_quantity"),
          classes: [
            "flux-element__quantity-block--positive",
            "bg-success-primary",
            "text-success-primary",
          ],
        });
      }
      if (enable_partial_op_validation && quantite_op_rebut) {
        quantities.push({
          icon: "box-crossed",
          value: +quantite_op_rebut,
          tooltip: t("scheduling.sidebar__header.reject_quantity"),
          classes: [
            "flux-element__quantity-block--negative",
            "bg-error-primary",
            "text-error-primary",
          ],
        });
      }

      return quantities;
    });

    return {
      OF_STATUS,
      clientParameters,
      variables,
      sendSchedulingLoadEventToBackend,
      dateFormat,
      getAssetURL,
      displayedOperationStatus,
      opDoneDate,
      shouldDisplayStatusAvailableAtInTooltip,
      handleOperationsStatusChangeConfirmation,
      fullOP,
      canUpdateStatus,
      displayedQuantities,
      isLoading,
      isOplitAdmin,
    };
  },
  computed: {
    computedErpDate() {
      return this.op?.erp_date ?? this.op?.initial_date;
    },
    computedDate() {
      return this.op?.new_date ?? this.op?.day_date;
    },
    computedFluxDate() {
      const {op, computedDate, opDoneDate} = this;
      const {
        of_delta,
        erp_date,
        day_date,
        first_active_op_theoric_date,
        first_active_op_planned_date,
        is_day_date_modified,
        all_ops_done,
        nb_ops_done,
      } = op || {};
      if (opDoneDate) return opDoneDate;
      const estimatedEndDate = getEstimatedEndDate(
        first_active_op_theoric_date,
        +of_delta,
        {
          initial_date: erp_date,
          planned_date: day_date,
          first_active_op_theoric_date,
          first_active_op_planned_date,
          is_day_date_modified,
          all_ops_done,
          has_of_started: !!nb_ops_done,
        },
      );
      return estimatedEndDate ?? opDoneDate ?? computedDate;
    },
    computedTimelineItemClasses(): Array<string> {
      const {
        isSelected,
        selectedOpId,
        initialOp: {op_id: initialOpId},
        op: {op_id: currentOpId, op_status: currentOpStatus},
        displayedOperationStatus,
      } = this;

      const classes: Array<string> = [];

      if (
        initialOpId === currentOpId &&
        initialOpId === selectedOpId &&
        !isSelected
      ) {
        classes.push("initial-op");
        return classes;
      }
      if (isSelected) classes.push(CSS_FLUX_SELECTED_OP_CLASS, "pt-3", "pb-4");
      if (displayedOperationStatus.text === "done" && !isSelected)
        classes.push("done-task");

      if ([OF_STATUS.POF, OF_STATUS.PENDING].includes(currentOpStatus))
        classes.push("flux_element__wrapper--pending");
      else classes.push(`flux_element__wrapper--${currentOpStatus}`);

      return classes;
    },
    lightOpacity() {
      const {
        isSelected,
        displayedOperationStatus: {text: operationStatus},
      } = this;
      return !isSelected && operationStatus === "done" ? "light-opacity" : "";
    },
    computedOperationRealDelta() {
      const {
        op: {erp_date},
        opDoneDate,
      } = this;
      if (!opDoneDate || !erp_date) return;
      return moment(opDoneDate).diff(erp_date, "days");
    },
    computedOperationIsLate() {
      const {computedOperationRealDelta} = this;
      if (!computedOperationRealDelta) return;
      return computedOperationRealDelta > 0;
    },
    computedOperationDelay() {
      const {computedOperationRealDelta, computedOperationIsLate} = this;
      if (!computedOperationRealDelta) return;

      return `(${
        computedOperationIsLate ? "+" : ""
      }${computedOperationRealDelta}${this.$t("global.day_letter")})`;
    },
    prettyOpDoneDate() {
      return moment(this.opDoneDate).format("DD/MM/YYYY");
    },
    computedOpName() {
      const {
        op: {op_name},
      } = this;
      return getReadableImportParsingRuleValue(op_name);
    },
  },
  methods: {
    async handleStatusChange(newStatus: string) {
      const {fullOP} = this;
      const update = {new_status: newStatus};

      const hasConfirmed = await this.handleOperationsStatusChangeConfirmation({
        operations: [fullOP],
        update,
      });
      if (!hasConfirmed) return;

      await this.setStatus(newStatus);
    },
    async setStatus(value: string) {
      this.isLoading = true;

      const {fullOP} = this;
      let update: Record<string, string | number> = {};
      update.new_status = value;
      if (value === "done") update.quantity_produced = +fullOP.quantite_of || 0;

      // We return insertion results to properly trigger recalculation of of_delta,
      // first_active_op_theoric_date and first_active_op_planned_date (needed to properly
      // calculate the updated estimated end date on change)
      const [{updated_values}, error] =
        await this.sendSchedulingLoadEventToBackend({
          data: [{initial: fullOP, update}],
          should_return_updated_values: true,
          should_return_insertion_results: true,
          force_pending_order_recalculation: true,
        });
      if (error) return this.$openSnackbar(null, "GENERIC_ERROR");

      const mappedValues = pgOpsMapFn(updated_values, {
        keepOpsDone: true,
      });
      this.propagateOpsChanges(mappedValues);

      this.isLoading = false;
    },
    forwardEvent(eventName = "", event: any = {}) {
      this.$emit(eventName, event);
    },
  },
});
</script>

<style scoped lang="scss">
.selected-op {
  background-color: rgb(var(--v-theme-grey-25));

  &.flux_element__wrapper {
    &--done {
      & :deep(.v-timeline-item__body) {
        background: radial-gradient(
          50% 90.71% at 100% 0%,
          rgb(var(--v-theme-grey-50)) 0%,
          rgb(var(--v-theme-grey-25)) 100%
        ) !important;
      }
    }

    &--on-going {
      & :deep(.v-timeline-item__body) {
        background: radial-gradient(
          50% 90.71% at 100% 0%,
          rgb(var(--v-theme-yellow-50)) 0%,
          rgb(var(--v-theme-grey-25)) 100%
        ) !important;
      }
    }

    &--available {
      & :deep(.v-timeline-item__body) {
        background: radial-gradient(
          50% 90.71% at 100% 0%,
          rgb(var(--v-theme-green-50)) 0%,
          rgb(var(--v-theme-grey-25)) 100%
        ) !important;
      }
    }

    &--pending {
      & :deep(.v-timeline-item__body) {
        background: radial-gradient(
          50% 90.71% at 100% 0%,
          rgb(var(--v-theme-purple-50)) 0%,
          rgb(var(--v-theme-grey-25)) 100%
        ) !important;
      }
    }
  }
}
.light-opacity {
  opacity: 0.23;
}

.flux-element__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.flux-element__quantity-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.flux-element__quantity-block {
  display: flex;
  align-items: center;
  height: 24px;
  border-radius: 4px;
  padding: 4px;

  &--neutral {
    color: rgb(var(--v-theme-grey-700));
  }

  &--positive {
    color: rgb(var(--v-theme-green-700));
  }

  &--negative {
    color: rgb(var(--v-theme-red-700));
  }
}
</style>
